import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { ORDER_DESK_LABELS_ID } from '../../../config/constants';
import { Box, Typography, Loader, Button, Badge, Slider } from '@lucid/lego';
import { useStore } from '../../../store/store';
import { useLabels } from '../../../hooks/useLabels';
import { useLocaleNavigate } from '../../../hooks/useLocaleNavigate';
import { convertToContentfulLocale } from '../../../utils/tools';
import type {
  ImporterOrder,
  VehicleOrder,
} from '../../../api/services/lupp/order/importer/dto.importer';
import { useImporterOrders } from './hooks/useImporterOrders';
import { useVehicleOrders } from './hooks/useVehicleOrders';
import {
  useApproveVehicleOrder,
  useBulkApproveVehicleOrders,
  useRejectVehicleOrder,
  useGetImporterOrder,
  useSearchImporterOrders,
  useGetVehicleOrder,
  useGetOrderNotifications,
  downloadOrderConfirmation,
} from '../../../api/services/lupp/order/importer/importer';
import { getImporterOrderStatusBadgeColor } from '../constants';
import Orders from './Orders/Orders';
import LineItems from './LineItems/LineItems';
import Notifications from './Notifications/Notifications';
import Details from './Details/Details';
import RejectNotificationModal from './Notifications/RejectNotificationModal/RejectNotificationModal';
import Deliveries from './Deliveries/Deliveries';
import DocumentsTab from './LineItems/DocumentsTab/DocumentsTab';
import styles from './OrderDesk.module.scss';

const getImporterOrderStatusLabel = (status: string, labels: Record<string, string>): string => {
  const map: Record<string, string> = {
    initiated: labels['status.initiated'] ?? 'Initiated',
    'partially fulfilled': labels['status.partiallyFilled'] ?? 'Partially Fulfilled',
    fulfilled: labels['status.fulfilled'] ?? 'Fulfilled',
    canceled: labels['status.canceled'] ?? 'Canceled',
    cancelled: labels['status.canceled'] ?? 'Canceled',
  };
  return (
    map[status.toLowerCase()] ??
    status
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

/**
 * Order Desk page component.
 * Three-tier navigation:
 * - `/order-hub/order-desk` — Importer orders list
 * - `/order-hub/order-desk/:importerOrderNumber` — Line items for a specific IO
 * - `/order-hub/order-desk/:importerOrderNumber/:orderNumber` — Vehicle order details
 * @component
 * @returns {JSX.Element}
 */
const OrderDesk: React.FC = () => {
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const navigate = useLocaleNavigate();
  const location = useLocation();
  const { importerOrderNumber, orderNumber } = useParams<{
    importerOrderNumber: string;
    orderNumber: string;
  }>();

  const isDeliveriesView = location.pathname.endsWith('/deliveries');

  const {
    data: labels,
    isLoading: labelsLoading,
    error: labelsError,
  } = useLabels({
    id: ORDER_DESK_LABELS_ID,
    locale: convertToContentfulLocale(locale),
  });

  /*
  |--------------------------------------------------------------------------
  | Importer orders table state
  |--------------------------------------------------------------------------
  */
  const {
    data: importerOrdersData,
    totalCount: importerOrdersTotalCount,
    statusGroupCounts: importerStatusGroupCounts,
    isLoading: importerOrdersLoading,
    currentPage: importerCurrentPage,
    pageSize: importerPageSize,
    searchQuery: importerSearchQuery,
    sortField: importerSortField,
    sortOrder: importerSortOrder,
    onPageChange: onImporterPageChange,
    onPageSizeChange: onImporterPageSizeChange,
    onSearchChange: onImporterSearchChange,
    onSort: onImporterSort,
    onStatusFilterChange: onImporterStatusFilterChange,
  } = useImporterOrders({ enabled: !labelsLoading && !labelsError });

  /*
  |--------------------------------------------------------------------------
  | Resolve selected importer order from URL param
  |--------------------------------------------------------------------------
  */
  const selectedImporterOrder = useMemo<ImporterOrder | undefined>(
    () =>
      importerOrderNumber
        ? importerOrdersData.find((o) => o.importerOrderNumber === importerOrderNumber)
        : undefined,
    [importerOrderNumber, importerOrdersData]
  );

  const isDetailView = Boolean(importerOrderNumber);
  const isVehicleDetailView = Boolean(importerOrderNumber && orderNumber && !isDeliveriesView);

  // Fallback: resolve the importer order by number when it's not in the current paginated page
  // (e.g. direct URL navigation without going through the orders table first)
  const { data: fallbackOrderData, isLoading: fallbackOrderLoading } = useSearchImporterOrders({
    searchString: importerOrderNumber,
    size: 1,
    enabled: isDetailView && !selectedImporterOrder && !labelsLoading && !labelsError,
  });

  const resolvedImporterOrder: ImporterOrder | undefined =
    selectedImporterOrder ?? fallbackOrderData?.items?.[0];

  /*
  |--------------------------------------------------------------------------
  | Fetch importer order detail (for fresh status in header)
  |--------------------------------------------------------------------------
  */
  const { data: importerOrderDetail } = useGetImporterOrder({
    importerOrderId: resolvedImporterOrder?.importerOrderId ?? null,
    enabled: isDetailView && Boolean(resolvedImporterOrder),
  });

  /*
  |--------------------------------------------------------------------------
  | Vehicle orders for detail view
  |--------------------------------------------------------------------------
  */
  const {
    data: vehicleOrdersData,
    isLoading: vehicleOrdersLoading,
    totalCount: vehicleTotalCount,
    statusGroupCounts: vehicleStatusGroupCounts,
    currentPage: vehicleCurrentPage,
    pageSize: vehiclePageSize,
    searchQuery: vehicleSearchQuery,
    sortField: vehicleSortField,
    sortOrder: vehicleSortOrder,
    onPageChange: onVehiclePageChange,
    onPageSizeChange: onVehiclePageSizeChange,
    onSearchChange: onVehicleSearchChange,
    onSort: onVehicleSort,
    onStatusFilterChange: onVehicleStatusFilterChange,
  } = useVehicleOrders({
    importerOrderId: resolvedImporterOrder?.importerOrderId ?? '',
    enabled: isDetailView && Boolean(resolvedImporterOrder),
  });

  /*
  |--------------------------------------------------------------------------
  | Resolve selected vehicle order from URL param
  |--------------------------------------------------------------------------
  */
  const { data: selectedVehicleOrder } = useGetVehicleOrder({
    vehicleOrderNumber: orderNumber ?? null,
    importerOrderId: resolvedImporterOrder?.importerOrderId ?? null,
    enabled: isVehicleDetailView,
  });

  // The vehicle-order detail endpoint does not return the review status, so carry
  // it over from the already-loaded line-items list (matched by order number).
  const resolvedVehicleOrder = useMemo<VehicleOrder | null>(() => {
    if (!selectedVehicleOrder) return null;
    if (selectedVehicleOrder.statusName) return selectedVehicleOrder;
    const lineItemStatus = vehicleOrdersData?.find(
      (item) => item.orderNumber === selectedVehicleOrder.orderNumber
    )?.statusName;
    return lineItemStatus
      ? { ...selectedVehicleOrder, statusName: lineItemStatus }
      : selectedVehicleOrder;
  }, [selectedVehicleOrder, vehicleOrdersData]);

  /*
  |--------------------------------------------------------------------------
  | Order notifications
  |--------------------------------------------------------------------------
  */
  const { data: notificationsData } = useGetOrderNotifications({
    enabled: !labelsLoading && !labelsError,
    importerOrderNumber: importerOrderNumber ?? null,
  });

  // De-dupe vehicles within each notification group — the API can return the same
  // vehicle assignment more than once (e.g. re-matched candidates), which would
  // otherwise render repeated Accept/Reject rows for the same child order.
  const notifications = useMemo(() => {
    const raw = notificationsData?.notifications ?? [];
    return raw.map((notification) => {
      const seen = new Set<string>();
      const dedupedVehicles = notification.vehicles.filter((vehicle) => {
        const key =
          vehicle.lineItemId ??
          `${vehicle.orderNumber}-${vehicle.orderlineItemNumber}-${vehicle.vin}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      return { ...notification, vehicles: dedupedVehicles };
    });
  }, [notificationsData]);

  /*
  |--------------------------------------------------------------------------
  | Approve / Reject mutations
  |--------------------------------------------------------------------------
  */
  const { mutate: approveVehicleOrder, isPending: isApproving } = useApproveVehicleOrder();
  const { mutate: bulkApproveVehicleOrders, isPending: isBulkApproving } =
    useBulkApproveVehicleOrders();
  const { mutate: rejectVehicleOrder, isPending: isRejecting } = useRejectVehicleOrder();

  const isActioning = isApproving || isBulkApproving || isRejecting;

  /*
  |--------------------------------------------------------------------------
  | Handlers
  |--------------------------------------------------------------------------
  */
  const handleImporterOrderClick = useCallback(
    (record: ImporterOrder) => {
      navigate(`/order-hub/order-desk/${record.importerOrderNumber}`);
    },
    [navigate]
  );

  const [isOrderConfirmationLoading, setIsOrderConfirmationLoading] = useState(false);

  const handleVehicleOrderConfirmationClick = useCallback((record: VehicleOrder) => {
    if (!record.orderNumber) return;
    setIsOrderConfirmationLoading(true);
    void downloadOrderConfirmation(record.orderNumber).finally(() => {
      setIsOrderConfirmationLoading(false);
    });
  }, []);

  const handleBackToOrders = useCallback(() => {
    navigate('/order-hub/order-desk');
  }, [navigate]);

  const handleBackToLineItems = useCallback(() => {
    navigate(`/order-hub/order-desk/${importerOrderNumber}`);
  }, [navigate, importerOrderNumber]);

  const handleGoToDeliveries = useCallback(
    (vehicleOrderNumber: string) => {
      const order = vehicleOrdersData?.find((o) => o.orderIdentifier === vehicleOrderNumber);

      // Derive IO number: loaded data → URL param → IO-{ID} direct → parse IOLI-{ID}-{SEQ} format
      let ioNumber = order?.importerOrderNumber ?? importerOrderNumber;
      if (!ioNumber) {
        const parts = vehicleOrderNumber.split('-');
        if (parts[0] === 'IO') {
          // vehicle.orderNumber from notifications is already the IO number
          ioNumber = vehicleOrderNumber;
        } else if (parts.length >= 3 && parts[0] === 'IOLI') {
          ioNumber = `IO-${parts.slice(1, -1).join('-')}`;
        }
      }

      if (ioNumber) {
        navigate(`/order-hub/order-desk/${ioNumber}/deliveries`);
      }
    },
    [navigate, importerOrderNumber, vehicleOrdersData]
  );

  const handleVehicleOrderSelect = useCallback(
    (record: VehicleOrder) => {
      const ioNumber = importerOrderNumber ?? record.importerOrderNumber;
      navigate(`/order-hub/order-desk/${ioNumber}/${record.orderNumber}`);
    },
    [navigate, importerOrderNumber]
  );

  const handleGoToDelivery = useCallback(
    (_record: VehicleOrder) => {
      navigate(`/order-hub/order-desk/${importerOrderNumber}/deliveries`);
    },
    [navigate, importerOrderNumber]
  );

  const handleAcceptVehicleOrders = useCallback(
    (orders: VehicleOrder[]) => {
      if (orders.length === 0) return;

      orders.forEach((o) => {
        if (o.importerOrderNumber && o.orderNumber) {
          approveVehicleOrder({
            orderNumber: o.importerOrderNumber,
            orderLineNumber: o.orderNumber,
          });
        }
      });
    },
    [approveVehicleOrder]
  );

  const [pendingRejectOrder, setPendingRejectOrder] = useState<VehicleOrder | null>(null);

  const handleRejectVehicleOrder = useCallback((order: VehicleOrder) => {
    setPendingRejectOrder(order);
  }, []);

  const handleConfirmReject = useCallback(
    (reason: string) => {
      if (pendingRejectOrder?.importerOrderNumber && pendingRejectOrder?.orderNumber) {
        rejectVehicleOrder({
          orderNumber: pendingRejectOrder.importerOrderNumber,
          orderLineNumber: pendingRejectOrder.orderNumber,
          reason,
        });
      }
      setPendingRejectOrder(null);
    },
    [pendingRejectOrder, rejectVehicleOrder]
  );

  const handleCloseRejectModal = useCallback(() => {
    setPendingRejectOrder(null);
  }, []);

  const handleAcceptNotificationItem = useCallback(
    (vehicleOrderNumber: string) => {
      const vehicle = notifications
        .flatMap((n) => n.vehicles)
        .find((v) => v.orderNumber === vehicleOrderNumber);
      if (vehicle?.orderNumber && vehicle?.orderlineItemNumber) {
        approveVehicleOrder({
          orderNumber: vehicle.orderNumber,
          orderLineNumber: vehicle.orderlineItemNumber,
        });
      }
    },
    [approveVehicleOrder, notifications]
  );

  const handleAcceptAllNotificationItems = useCallback(
    (notification: (typeof notifications)[number]) => {
      const distinctOrderNumbers = [
        ...new Set(notification.vehicles.map((v) => v.orderNumber).filter(Boolean)),
      ] as string[];
      distinctOrderNumbers.forEach((orderNumber) => {
        bulkApproveVehicleOrders({ orderNumber });
      });
    },
    [bulkApproveVehicleOrders]
  );

  const handleRejectNotificationItem = useCallback(
    (vehicleOrderNumber: string, reason: string) => {
      const vehicle = notifications
        .flatMap((n) => n.vehicles)
        .find((v) => v.orderNumber === vehicleOrderNumber);
      if (vehicle?.orderNumber && vehicle?.orderlineItemNumber) {
        rejectVehicleOrder({
          orderNumber: vehicle.orderNumber,
          orderLineNumber: vehicle.orderlineItemNumber,
          reason,
        });
      }
    },
    [rejectVehicleOrder, notifications]
  );

  const handleNotificationItemClick = useCallback(
    (vehicleOrderNumber: string) => {
      const order = vehicleOrdersData?.find((o) => o.orderNumber === vehicleOrderNumber);

      // Derive IO number: loaded data → URL param → parse IOLI-{ID}-{SEQ} format → IO-{ID}
      let ioNumber = order?.importerOrderNumber ?? importerOrderNumber;
      if (!ioNumber) {
        const parts = vehicleOrderNumber.split('-');
        if (parts.length >= 3 && parts[0] === 'IOLI') {
          ioNumber = `IO-${parts.slice(1, -1).join('-')}`;
        }
      }

      if (ioNumber) {
        navigate(`/order-hub/order-desk/${ioNumber}/${vehicleOrderNumber}`);
      }
    },
    [navigate, vehicleOrdersData, importerOrderNumber]
  );

  const resolvedLabels: Record<string, string> = useMemo(() => labels ?? {}, [labels]);

  const [documentsSliderOpen, setDocumentsSliderOpen] = useState(false);

  /*
  |--------------------------------------------------------------------------
  | Loading / error states
  |--------------------------------------------------------------------------
  */
  if (
    labelsLoading ||
    importerOrdersLoading ||
    (isDetailView && !resolvedImporterOrder && fallbackOrderLoading)
  ) {
    return (
      <Box testId="order-desk-page">
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          {resolvedLabels['page.title'] ?? 'Order Desk'}
        </Typography>
        <Box className={styles.loaderContainer}>
          <Loader size={48} theme={theme} />
        </Box>
      </Box>
    );
  }

  if (labelsError) {
    return (
      <Box testId="order-desk-page">
        <Box pt={24}>
          <Typography variant="body" size="large" theme={theme} color="error-1">
            {resolvedLabels['error.loadingLabels'] ??
              'Something went wrong while loading this page. Please refresh and try again.'}
          </Typography>
        </Box>
      </Box>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Render helpers
  |--------------------------------------------------------------------------
  */
  const renderHeader = () => {
    if (isDetailView && !isVehicleDetailView && !isDeliveriesView && resolvedImporterOrder) {
      return (
        <>
          <Box className={styles.detailHeader}>
            <Box className={styles.detailHeaderLeft}>
              <Box className={styles.detailHeaderTitle}>
                <Typography variant="headline" size="small" color="primary-1" theme={theme}>
                  {(
                    resolvedLabels['page.orderTitle'] ??
                    `Order ${resolvedImporterOrder.importerOrderNumber}`
                  ).replace('{{orderNumber}}', resolvedImporterOrder.importerOrderNumber ?? '')}
                </Typography>
                {(importerOrderDetail?.statusName ?? resolvedImporterOrder.statusName) && (
                  <Badge
                    label={getImporterOrderStatusLabel(
                      importerOrderDetail?.statusName ?? resolvedImporterOrder.statusName ?? '',
                      resolvedLabels
                    )}
                    type="primary"
                    color={getImporterOrderStatusBadgeColor(
                      importerOrderDetail?.statusName ?? resolvedImporterOrder.statusName ?? ''
                    )}
                    theme={theme === 'dark' ? 'light' : 'dark'}
                  />
                )}
              </Box>
              <Box pt={8} pb={16}>
                <Typography variant="body" size="medium" color="gray-3" theme={theme}>
                  {resolvedLabels['page.lineItemsSubtitle'] ??
                    'View all line items for this order below.'}
                </Typography>
              </Box>
              <Button
                type="tertiary"
                color="neutral"
                theme={theme}
                dataTestId="back-to-order-desk-button"
                gaComponent="order-desk"
                label={resolvedLabels['page.back'] ?? 'Back to Order Desk'}
                iconLeft={{ category: 'arrows', name: 'arrow-left' }}
                handleClick={handleBackToOrders}
              />
            </Box>
            <Box pt={2} pr={2} className={styles.detailHeaderActions}>
              <Button
                type="secondary"
                color="neutral"
                theme={theme}
                dataTestId="view-documents-button"
                gaComponent="order-desk"
                label={resolvedLabels['page.viewDocuments'] ?? 'View Documents'}
                handleClick={() => setDocumentsSliderOpen(true)}
              />
              <Button
                type="primary"
                color="neutral"
                theme={theme}
                dataTestId="view-deliveries-button"
                gaComponent="order-desk"
                label={resolvedLabels['page.viewDeliveries'] ?? 'View Deliveries'}
                iconLeft={{ category: 'global', name: 'truck' }}
                handleClick={() =>
                  navigate(`/order-hub/order-desk/${importerOrderNumber}/deliveries`)
                }
              />
            </Box>
          </Box>
          <Box pb={24} />
        </>
      );
    }

    if (!isVehicleDetailView && !isDeliveriesView) {
      return (
        <>
          <Typography variant="headline" size="small" color="primary-1" theme={theme}>
            {resolvedLabels['page.title'] ?? 'Order Desk'}
          </Typography>
          <Box pt={8} pb={24}>
            <Typography variant="body" size="medium" color="gray-3" theme={theme}>
              {resolvedLabels['page.subtitle'] ?? 'Manage and track all bulk orders.'}
            </Typography>
          </Box>
        </>
      );
    }

    return null;
  };

  const renderContent = () => {
    if (isDeliveriesView) {
      return (
        <Deliveries
          theme={theme}
          labels={resolvedLabels}
          importerOrderId={resolvedImporterOrder?.importerOrderId ?? null}
          importerOrderNumber={importerOrderNumber}
          onBack={handleBackToLineItems}
          fileReferences={importerOrderDetail?.moduleFileReferenceDtoList ?? []}
        />
      );
    }

    if (isVehicleDetailView) {
      return (
        <Details
          theme={theme}
          order={resolvedVehicleOrder}
          labels={resolvedLabels}
          onAccept={(order) => handleAcceptVehicleOrders([order])}
          onReject={handleRejectVehicleOrder}
          onGoToDelivery={handleGoToDelivery}
          onOrderConfirmation={handleVehicleOrderConfirmationClick}
          onBack={handleBackToLineItems}
          isAccepting={isApproving || isBulkApproving}
          isRejecting={isRejecting}
        />
      );
    }

    if (isDetailView) {
      if (vehicleOrdersLoading || (!resolvedImporterOrder && fallbackOrderLoading)) {
        return (
          <Box className={styles.loaderContainer}>
            <Loader size={48} theme={theme} />
          </Box>
        );
      }
      return (
        <LineItems
          theme={theme}
          data={vehicleOrdersData}
          totalCount={vehicleTotalCount}
          statusGroupCounts={vehicleStatusGroupCounts}
          currentPage={vehicleCurrentPage}
          pageSize={vehiclePageSize}
          onPageChange={onVehiclePageChange}
          onPageSizeChange={onVehiclePageSizeChange}
          labels={resolvedLabels}
          searchQuery={vehicleSearchQuery}
          onSearchChange={onVehicleSearchChange}
          sortColumn={vehicleSortField}
          sortDirection={vehicleSortOrder}
          onSort={onVehicleSort}
          onStatusFilterChange={onVehicleStatusFilterChange}
          onVehicleOrderSelect={handleVehicleOrderSelect}
          onAcceptSelected={handleAcceptVehicleOrders}
          onRejectSingle={handleRejectVehicleOrder}
          onGoToDelivery={handleGoToDelivery}
          onOrderConfirmationClick={handleVehicleOrderConfirmationClick}
          isActioning={isActioning}
        />
      );
    }

    return (
      <Orders
        theme={theme}
        data={importerOrdersData}
        totalCount={importerOrdersTotalCount}
        statusGroupCounts={importerStatusGroupCounts}
        currentPage={importerCurrentPage}
        pageSize={importerPageSize}
        onPageChange={onImporterPageChange}
        onPageSizeChange={onImporterPageSizeChange}
        labels={resolvedLabels}
        searchQuery={importerSearchQuery}
        onSearchChange={onImporterSearchChange}
        sortColumn={importerSortField}
        sortDirection={importerSortOrder}
        onSort={onImporterSort}
        onStatusFilterChange={onImporterStatusFilterChange}
        onImporterOrderClick={handleImporterOrderClick}
      />
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */
  return (
    <Box testId="order-desk-page">
      {renderHeader()}

      {/* Notifications — hidden on vehicle detail/deliveries */}
      {!isVehicleDetailView && !isDeliveriesView && (
        <Notifications
          theme={theme}
          notifications={notifications}
          onAcceptItem={handleAcceptNotificationItem}
          onAcceptAll={handleAcceptAllNotificationItems}
          onRejectItem={handleRejectNotificationItem}
          onItemClick={handleNotificationItemClick}
          onGoToDelivery={handleGoToDeliveries}
          isRejectPending={isRejecting}
          dataTestId="order-notifications"
          labels={resolvedLabels}
        />
      )}

      {renderContent()}

      <RejectNotificationModal
        dataTestId="reject-order-modal"
        isOpen={pendingRejectOrder !== null}
        onClose={handleCloseRejectModal}
        onSubmit={handleConfirmReject}
        isPending={isRejecting}
        theme="light"
      />

      {/* Documents slider */}
      <Slider
        isOpen={documentsSliderOpen}
        onClose={() => setDocumentsSliderOpen(false)}
        title={resolvedLabels['slider.documents'] ?? 'Documents'}
        width="1000px"
        theme={theme === 'light' ? 'dark' : 'light'}
        direction="right"
      >
        <DocumentsTab
          theme={theme === 'light' ? 'dark' : 'light'}
          labels={resolvedLabels}
          documents={importerOrderDetail?.moduleFileReferenceDtoList ?? []}
        />
      </Slider>

      {isOrderConfirmationLoading && (
        <Box
          className={styles.orderConfirmationLoaderOverlay}
          data-testid="final-invoice-loader-overlay"
        >
          <Loader size={48} theme={theme} />
        </Box>
      )}
    </Box>
  );
};

export default OrderDesk;
